import { Fragment } from "react";
import { Box } from "@mui/material";

const INK = "#1F2937";
const MUTED = "#6B7280";
const ZERO_BG = "#F7F9FB";

type Ramp = { light: [number, number, number]; dark: [number, number, number] };

// One sequential single-hue ramp per measure — hue = measure identity,
// darkness = magnitude. Blue / green / amber are CVD-distinct.
const MEASURE_RAMPS: Record<string, Ramp> = {
  Released: { light: [234, 242, 253], dark: [24, 79, 149] }, // blue
  Utilised: { light: [230, 244, 230], dark: [11, 90, 11] }, // green
  Utilized: { light: [230, 244, 230], dark: [11, 90, 11] }, // (spelling alias)
  // Money released but not yet spent — orange, so it reads as "sitting idle"
  // rather than sharing the blue of Released. Keyed under both names: the
  // dashboard renames this row Unutilised, other callers may still say Balance.
  Unutilised: { light: [255, 240, 224], dark: [194, 91, 10] }, // orange
  Unutilized: { light: [255, 240, 224], dark: [194, 91, 10] }, // (spelling alias)
  Balance: { light: [255, 240, 224], dark: [194, 91, 10] }, // orange
  // Physical/financial work booked — purple, the 4th CVD-distinct hue, so it
  // does not read as a variant of the three money rows above it.
  "Fin. Progress": { light: [240, 235, 250], dark: [91, 44, 145] }, // purple
};
const DEFAULT_RAMP: Ramp = { light: [234, 242, 253], dark: [24, 79, 149] };
const rampFor = (measure: string) => MEASURE_RAMPS[measure] ?? DEFAULT_RAMP;

type MeasureRow = {
  measure: string;
  values: number[];
  total?: number;
  /** Fund applications (FAs) behind each cell, parallel to `values`. Optional
   *  — callers that pass nothing render amounts only, exactly as before. */
  counts?: number[];
  countTotal?: number;
};
type StateGroup = {
  state: string;
  rows: MeasureRow[];
  /** Total fund applications (FAs) in the state, shown on the group header.
   *  Optional. */
  count?: number;
};

type AmountHeatmapProps = {
  /** One group per state; each group has its measure rows (Released / Utilised
   *  / Balance) with a value per FA year. */
  groups: StateGroup[];
  years: number[]; // FA-year column labels
  /** Unused now (kept for compatibility) — scale is per-measure. */
  max?: number;
  /** Split the states into N side-by-side sub-tables to shorten the height. */
  columns?: number;
  showTotal?: boolean;
  valueFormatter?: (v: number) => string;
};

const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);

// sqrt eases the ramp so smaller cells stay visible next to a dominant one
// (one year can be 10× the rest) — the exact value is still in the cell.
const intensity = (v: number, max: number) =>
  !v || max <= 0 ? 0 : Math.pow(v / max, 0.55);

const cellBg = (v: number, max: number, ramp: Ramp) => {
  const t = intensity(v, max);
  if (t === 0) return ZERO_BG;
  return `rgb(${lerp(ramp.light[0], ramp.dark[0], t)},${lerp(
    ramp.light[1],
    ramp.dark[1],
    t,
  )},${lerp(ramp.light[2], ramp.dark[2], t)})`;
};

const cellFg = (v: number, max: number) =>
  !v ? "#B6BDC4" : intensity(v, max) > 0.55 ? "#fff" : INK;

// "FA" (Fund Application) matches the abbreviation used elsewhere on the
// Fund Dashboard (FA Year filter, FA1-FA10) rather than the generic "app".
const appWord = (n: number) => (n === 1 ? "FA" : "FAs");

/** Fund applications (FAs) behind the amount, on a second line inside the
 *  cell. Inherits the cell's colour (which flips to white on dark fills) and
 *  is dimmed so the rupee figure stays the thing you read first. */
const CountLine = ({ n }: { n: number }) => (
  <Box
    component="span"
    sx={{
      display: "block",
      fontSize: 9,
      fontWeight: 500,
      opacity: 0.72,
      lineHeight: 1.25,
    }}
  >
    {n.toLocaleString("en-IN")} FA{n === 1 ? "" : "s"}
  </Box>
);

const AmountHeatmap = ({
  groups,
  years,
  columns = 1,
  showTotal = true,
  valueFormatter = (v) => v.toLocaleString("en-IN"),
}: AmountHeatmapProps) => {
  if (!groups.length || !years.length) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: MUTED,
          fontSize: 13,
        }}
      >
        No amounts released yet.
      </Box>
    );
  }

  // Per-measure colour scale (global across every state so both sub-tables
  // share the same scale): each measure has its own hue and its own max.
  const measureOrder: string[] = [];
  const measureMax = new Map<string, number>();
  groups.forEach((g) =>
    g.rows.forEach((r) => {
      if (!measureMax.has(r.measure)) {
        measureMax.set(r.measure, 0);
        measureOrder.push(r.measure);
      }
      const rowMax = Math.max(0, ...r.values);
      measureMax.set(r.measure, Math.max(measureMax.get(r.measure)!, rowMax));
    }),
  );

  const cell = {
    fontSize: 11,
    fontWeight: 600,
    textAlign: "center" as const,
    py: 0.7,
    px: 0.5,
    borderRadius: "4px",
    whiteSpace: "nowrap" as const,
  };

  // Split states into `columns` roughly-equal side-by-side sub-tables.
  const columnCount = Math.max(1, columns);
  const perCol = Math.ceil(groups.length / columnCount);
  const chunks = Array.from({ length: columnCount }, (_, i) =>
    groups.slice(i * perCol, (i + 1) * perCol),
  ).filter((c) => c.length);

  // Cells carry a second line ("12 FAs") when counts are supplied, so the
  // columns need a little more room than the amount alone required.
  const hasCounts = groups.some((g) => g.rows.some((r) => !!r.counts));
  const yearColMin = hasCounts ? 60 : 52;
  const totalColMin = hasCounts ? 72 : 64;
  const gridCols = `minmax(84px, 1fr) repeat(${years.length}, minmax(${yearColMin}px, 1fr))${
    showTotal ? ` minmax(${totalColMin}px, 0.9fr)` : ""
  }`;

  const renderGrid = (chunk: StateGroup[], key: number) => (
    <Box
      key={key}
      sx={{
        display: "grid",
        gridTemplateColumns: gridCols,
        gap: "3px",
        flex: 1,
        minWidth: 270,
        alignContent: "start",
      }}
    >
      {/* header row */}
      <Box sx={{ ...cell, color: MUTED, textAlign: "left" }} />
      {years.map((y) => (
        <Box key={y} sx={{ ...cell, color: MUTED }}>
          FA{y}
        </Box>
      ))}
      {showTotal && (
        <Box sx={{ ...cell, color: MUTED, fontWeight: 700 }}>Total</Box>
      )}

      {chunk.map((g) => (
        <Fragment key={g.state}>
          <Box
            sx={{
              gridColumn: "1 / -1",
              fontSize: 11,
              fontWeight: 700,
              color: INK,
              background: "#F3F6F9",
              borderRadius: "4px",
              px: 1,
              py: 0.5,
              mt: 0.5,
              overflow: "hidden",
              whiteSpace: "nowrap",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
            title={
              g.count === undefined
                ? g.state
                : `${g.state} — ${g.count} ${appWord(g.count)}`
            }
          >
            <Box
              component="span"
              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {g.state}
            </Box>
            {g.count !== undefined && (
              <Box
                component="span"
                sx={{ fontSize: 10, fontWeight: 600, color: MUTED }}
              >
                {g.count.toLocaleString("en-IN")} FA
                {g.count === 1 ? "" : "s"}
              </Box>
            )}
          </Box>
          {g.rows.map((row) => {
            const ramp = rampFor(row.measure);
            const mMax = measureMax.get(row.measure) ?? 0;
            return (
              <Fragment key={row.measure}>
                <Box
                  sx={{
                    ...cell,
                    textAlign: "left",
                    color: INK,
                    fontWeight: 600,
                    pl: 0.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "2px",
                      flexShrink: 0,
                      background: `rgb(${ramp.dark[0]},${ramp.dark[1]},${ramp.dark[2]})`,
                    }}
                  />
                  {row.measure}
                </Box>
                {years.map((y, ci) => {
                  const v = row.values[ci] ?? 0;
                  const n = row.counts?.[ci];
                  return (
                    <Box
                      key={y}
                      sx={{
                        ...cell,
                        background: cellBg(v, mMax, ramp),
                        color: cellFg(v, mMax),
                      }}
                      title={`${g.state} · ${row.measure} · FA${y}: ${valueFormatter(
                        v,
                      )}${n === undefined ? "" : ` across ${n} ${appWord(n)}`}`}
                    >
                      {v ? valueFormatter(v) : "–"}
                      {v !== 0 && !!n && <CountLine n={n} />}
                    </Box>
                  );
                })}
                {showTotal && (
                  <Box
                    sx={{
                      ...cell,
                      background: "#EEF2F6",
                      color: INK,
                      fontWeight: 700,
                    }}
                    title={`${g.state} · ${row.measure} total: ${valueFormatter(
                      row.total ?? 0,
                    )}${
                      row.countTotal === undefined
                        ? ""
                        : ` across ${row.countTotal} ${appWord(row.countTotal)}`
                    }`}
                  >
                    {row.total ? valueFormatter(row.total) : "–"}
                    {!!row.total && !!row.countTotal && (
                      <CountLine n={row.countTotal} />
                    )}
                  </Box>
                )}
              </Fragment>
            );
          })}
        </Fragment>
      ))}
    </Box>
  );

  return (
    // height:100% so the card can stretch us to match a neighbouring column;
    // overflowY:auto so that if the card is ever SHORTER than the table (many
    // states, short neighbour) the rows scroll instead of being clipped away.
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Box sx={{ display: "flex", gap: 2.5, overflowX: "auto", alignItems: "flex-start" }}>
        {chunks.map((chunk, i) => renderGrid(chunk, i))}
      </Box>

      {/* per-measure colour-scale legend (one hue per measure, light → dark) */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          // mt:auto pins the legend to the bottom of a stretched card, so the
          // slack shows up between table and legend rather than below both.
          mt: "auto",
          pt: 1.5,
          flexShrink: 0,
          borderTop: "1px solid #F0F0F0",
          fontSize: 11,
          color: MUTED,
        }}
      >
        {measureOrder.map((m) => {
          const ramp = rampFor(m);
          const mMax = measureMax.get(m) ?? 0;
          return (
            <Box
              key={m}
              sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            >
              <Box sx={{ color: INK, fontWeight: 600 }}>{m}</Box>
              <Box
                sx={{
                  width: 68,
                  height: 8,
                  borderRadius: "4px",
                  background: `linear-gradient(to right, ${cellBg(
                    0.06 * mMax,
                    mMax,
                    ramp,
                  )}, ${cellBg(mMax, mMax, ramp)})`,
                }}
              />
              <Box>up to {valueFormatter(mMax)}</Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AmountHeatmap;
